import { CardInVariant, TemplateInVariant, Variant } from '@space-cow-media/spellbook-client';

export type ComboPiece = { key: string } & ({ card: CardInVariant } | { template: TemplateInVariant });

export interface Replacement {
  piece: ComboPiece;
  variant: Variant;
  /* What else comes in with the piece, one set per variant, when no variant swaps it in alone. */
  companions: ComboPiece[][];
}

export interface ReplaceablePiece {
  piece: ComboPiece;
  replacements: Replacement[];
}

interface Swap {
  variant: Variant;
  companions: ComboPiece[];
}

export const comboPieces = (variant: Variant): ComboPiece[] => [
  ...variant.uses.map((card) => ({ key: `card ${card.quantity} ${card.card.name.toLowerCase()}`, card })),
  ...variant.requires.map((template) => ({ key: `template ${template.quantity} ${template.template.id}`, template })),
];

const missingFrom = (pieces: ComboPiece[], others: ComboPiece[]) =>
  pieces.filter(({ key }) => !others.some((other) => other.key === key));

export interface VariantGroup {
  generator?: number;
  variants: Variant[];
}

export type VariantGrouping = 'ungrouped' | 'combo' | 'card';

/* Variants found for a deck that have the same standing, such as missing one card, and what else they take. */
export interface VariantCategory {
  label: string;
  detail?: string;
  variants: Variant[];
  /* The deck has every card of them, so only how they differ from each other stands out. */
  inDeck?: boolean;
}

export const isOfCombo = (variant: Variant, generator?: number) => variant.of.some(({ id }) => id === generator);

/* A variant generated by several combos joins the one with the most variants here, so that it shows up once. */
export function groupVariantsByCombo(variants: Variant[]): VariantGroup[] {
  const generatorSizes = new Map<number, number>();
  variants.forEach((variant) =>
    variant.of.forEach(({ id }) => generatorSizes.set(id, (generatorSizes.get(id) ?? 0) + 1)),
  );
  const size = (id: number) => generatorSizes.get(id) ?? 0;

  const groups = new Map<number | string, VariantGroup>();
  variants.forEach((variant) => {
    const generator = variant.of
      .map(({ id }) => id)
      .reduce<number | undefined>(
        (best, id) =>
          best === undefined || size(id) > size(best) || (size(id) === size(best) && id < best) ? id : best,
        undefined,
      );
    const key = generator ?? variant.id;
    const group = groups.get(key) ?? { generator, variants: [] };
    group.variants.push(variant);
    groups.set(key, group);
  });
  return Array.from(groups.values());
}

/* Two variants that differ by a single piece each show that those two pieces play the same role, which tells
   which piece another variant replaces when it swaps several at once. */
function pieceRoles(pieceSets: ComboPiece[][]): (key: string) => string {
  const parents = new Map<string, string>();
  const role = (key: string): string => {
    const parent = parents.get(key);
    if (parent === undefined) {
      return key;
    }
    const root = role(parent);
    parents.set(key, root);
    return root;
  };

  pieceSets.forEach((pieces, index) =>
    pieceSets.slice(index + 1).forEach((others) => {
      const removed = missingFrom(pieces, others);
      const added = missingFrom(others, pieces);
      if (removed.length === 1 && added.length === 1 && role(removed[0].key) !== role(added[0].key)) {
        parents.set(role(added[0].key), role(removed[0].key));
      }
    }),
  );
  return role;
}

const uniqueSets = (sets: ComboPiece[][]) =>
  sets.filter(
    (set, index) =>
      sets.findIndex((other) => other.map(({ key }) => key).join() === set.map(({ key }) => key).join()) === index,
  );

/* The pieces of the first variant, each with the pieces the other variants put in its place. */
export function variantReplacements(variants: Variant[]): ReplaceablePiece[] {
  const pieceSets = variants.map(comboPieces);
  const [basePieces] = pieceSets;
  const role = pieceRoles(pieceSets);
  const swaps = new Map<string, Map<string, { piece: ComboPiece; swaps: Swap[] }>>();

  pieceSets.forEach((pieces, index) => {
    const removed = missingFrom(basePieces, pieces);
    const added = missingFrom(pieces, basePieces);
    added.forEach((piece) => {
      const replaced = removed.find(({ key }) => role(key) === role(piece.key)) ?? removed[0];
      if (!replaced) {
        return;
      }
      const replacements = swaps.get(replaced.key) ?? new Map();
      swaps.set(replaced.key, replacements);
      const replacement = replacements.get(piece.key) ?? { piece, swaps: [] };
      replacements.set(piece.key, replacement);
      replacement.swaps.push({ variant: variants[index], companions: added.filter((other) => other !== piece) });
    });
  });

  return basePieces.map((piece) => ({
    piece,
    replacements: Array.from(swaps.get(piece.key)?.values() ?? [])
      .map(({ piece, swaps }) => {
        const alone = swaps.find(({ companions }) => !companions.length);
        return {
          piece,
          variant: (alone ?? swaps[0]).variant,
          companions: alone ? [] : uniqueSets(swaps.map(({ companions }) => companions)),
        };
      })
      .sort(
        (left, right) =>
          Number(left.companions.length > 0) - Number(right.companions.length > 0) ||
          variants.indexOf(left.variant) - variants.indexOf(right.variant),
      ),
  }));
}
